type Card = HTMLElement & { dataset: { widgetKey: string } }

const DROP_RING = ["outline", "outline-2", "outline-primary", "-outline-offset-2"]

// 8-dot 2×4 grip icon
const GRIP_SVG = `<svg viewBox="0 0 8 18" width="8" height="18" fill="currentColor" aria-hidden="true">
  <circle cx="2" cy="2"   r="1.4"/>
  <circle cx="6" cy="2"   r="1.4"/>
  <circle cx="2" cy="6.5" r="1.4"/>
  <circle cx="6" cy="6.5" r="1.4"/>
  <circle cx="2" cy="11"  r="1.4"/>
  <circle cx="6" cy="11"  r="1.4"/>
  <circle cx="2" cy="15.5" r="1.4"/>
  <circle cx="6" cy="15.5" r="1.4"/>
</svg>`

export const DashboardGrid = {
  _dragSrc: null as Card | null,

  mounted(this: typeof DashboardGrid & { el: HTMLElement }) {
    this._restore()
    this._bindDrag()
  },

  updated(this: typeof DashboardGrid & { el: HTMLElement }) {
    this._restore()
  },

  _storageKey(this: typeof DashboardGrid & { el: HTMLElement }) {
    return `dashboard-grid:${this.el.id}`
  },

  _cards(this: typeof DashboardGrid & { el: HTMLElement }): Card[] {
    return Array.from(this.el.children).filter(
      (el): el is Card => !!(el as HTMLElement).dataset?.widgetKey,
    ) as Card[]
  },

  _loadOrder(this: typeof DashboardGrid & { el: HTMLElement }): string[] {
    try {
      return JSON.parse(localStorage.getItem(this._storageKey()) ?? "[]")
    } catch {
      return []
    }
  },

  _saveOrder(this: typeof DashboardGrid & { el: HTMLElement }) {
    localStorage.setItem(
      this._storageKey(),
      JSON.stringify(this._cards().map((c) => c.dataset.widgetKey)),
    )
  },

  _ensureGrip(card: Card) {
    if (card.querySelector(".dnd-grip")) return
    card.style.position = "relative"

    const grip = document.createElement("button")
    grip.type = "button"
    grip.className = "dnd-grip"
    grip.setAttribute("aria-label", "Drag to reorder")
    grip.setAttribute("title", "Drag to reorder")
    Object.assign(grip.style, {
      position: "absolute",
      top: "10px",
      right: "10px",
      zIndex: "20",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "26px",
      height: "26px",
      borderRadius: "5px",
      border: "1px solid transparent",
      background: "transparent",
      color: "var(--color-muted-foreground, #9ca3af)",
      opacity: "0",
      cursor: "grab",
      transition: "opacity 120ms ease, background 120ms ease, border-color 120ms ease",
    })
    grip.innerHTML = GRIP_SVG

    card.addEventListener("mouseenter", () => {
      grip.style.opacity = "1"
    })
    card.addEventListener("mouseleave", () => {
      if (this._dragSrc !== card) grip.style.opacity = "0"
    })
    grip.addEventListener("mouseover", () => {
      grip.style.background = "var(--color-accent, rgba(0,0,0,0.06))"
      grip.style.borderColor = "var(--color-border, rgba(0,0,0,0.1))"
    })
    grip.addEventListener("mouseleave", () => {
      grip.style.background = "transparent"
      grip.style.borderColor = "transparent"
    })

    // Only allow drag to start from the grip handle
    grip.addEventListener("mousedown", () => {
      card.draggable = true
    })
    grip.addEventListener("mouseup", () => {
      // reset handled by dragend
    })

    card.appendChild(grip)
  },

  _restore(this: typeof DashboardGrid & { el: HTMLElement }) {
    const order = this._loadOrder()
    const cards = this._cards()

    cards.forEach((c) => {
      c.draggable = false // only enabled on grip mousedown
      this._ensureGrip(c)
    })

    if (!order.length) return

    const byKey = new Map(cards.map((c) => [c.dataset.widgetKey, c]))
    const ordered = order.map((k) => byKey.get(k)).filter(Boolean) as Card[]
    const known = new Set(order)
    const extras = cards.filter((c) => !known.has(c.dataset.widgetKey))
    ;[...ordered, ...extras].forEach((c) => this.el.appendChild(c))
  },

  _bindDrag(this: typeof DashboardGrid & { el: HTMLElement }) {
    const el = this.el

    el.addEventListener("dragstart", (e) => {
      const src = (e.target as HTMLElement).closest<Card>("[data-widget-key]")
      if (!src) return
      this._dragSrc = src
      src.style.opacity = "0.4"
      e.dataTransfer!.effectAllowed = "move"
      // Show grip during drag
      const grip = src.querySelector<HTMLElement>(".dnd-grip")
      if (grip) grip.style.opacity = "1"
    })

    el.addEventListener("dragover", (e) => {
      e.preventDefault()
      e.dataTransfer!.dropEffect = "move"
      const target = (e.target as HTMLElement).closest<Card>("[data-widget-key]")
      if (!target || target === this._dragSrc) return
      this._cards().forEach((c) => c.classList.remove(...DROP_RING))
      target.classList.add(...DROP_RING)
    })

    el.addEventListener("dragleave", (e) => {
      if (!el.contains(e.relatedTarget as Node)) {
        this._cards().forEach((c) => c.classList.remove(...DROP_RING))
      }
    })

    el.addEventListener("drop", (e) => {
      e.preventDefault()
      const target = (e.target as HTMLElement).closest<Card>("[data-widget-key]")
      const src = this._dragSrc
      if (!target || !src || target === src) return
      target.classList.remove(...DROP_RING)

      const cards = this._cards()
      const si = cards.indexOf(src)
      const ti = cards.indexOf(target)
      if (si < ti) target.after(src)
      else target.before(src)

      this._saveOrder()
    })

    el.addEventListener("dragend", () => {
      if (this._dragSrc) {
        this._dragSrc.style.opacity = ""
        this._dragSrc.draggable = false
        const grip = this._dragSrc.querySelector<HTMLElement>(".dnd-grip")
        if (grip) grip.style.opacity = "0"
        this._dragSrc = null
      }
      this._cards().forEach((c) => c.classList.remove(...DROP_RING))
    })
  },
}
